import { useEffect, useState } from 'react';

import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { FormikProps } from 'formik';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { useDispatch, useSelector } from 'react-redux';
import {
  doClearDeleteAccountApplicationDocument,
  doDeleteAccountApplicationDocument,
  doCancelAccountApplicationDocument,
  doRefreshAccountApplication,
  doClearCancelAccountApplicationDocument,
} from 'src/actions';
import { ErrorCode } from 'src/constants';
import { getClientApi } from 'src/data-communication';
import { UnknownError } from 'src/errors';
import { useAccountSelector } from 'src/hooks';
import { ConfirmActionModal, MUpload } from 'src/lib';
import { AccountApplicationCategoryView } from 'src/models';
import { validateFile } from 'src/utils';

import { AccountApplicationFormValues } from './AccountApplicationForm';

const clientApi = getClientApi();

interface AccountApplicationCategoryDocumentProps {
  title?: string;
  label?: string;
  category: AccountApplicationCategoryView;
  form: FormikProps<AccountApplicationFormValues>;
  accept?: string[];
  isCancelable?: boolean;
  isDeletable?: boolean;
  testId?: string;
}

export const AccountApplicationCategoryDocument = ({
  title,
  label,
  category,
  accept = ['application/pdf'],
  form,
  isCancelable = true,
  isDeletable = true,
  testId,
}: AccountApplicationCategoryDocumentProps) => {
  const dispatch = useDispatch();
  const authToken: string = useSelector((state: any) => state.auth?.data?.authToken);
  const [documentType, setDocumentType] = useState<string>(category.requestedDocuments[0].value);
  const [documentTypeError, setDocumentTypeError] = useState<string>();
  const [originalFilename, setOriginalFilename] = useState<string>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const [fileSize, setFileSize] = useState<number>(0);
  const [fileList, setFileList] = useState<{ name: string; uid: string }[]>([]);
  const [isDeleteConfirmModalVisible, setIsDeleteConfirmModalVisible] = useState<boolean>(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string>();
  const [deleteMode, setDeleteMode] = useState<'cancel' | 'remove'>();

  const isDeleteDocumentLoading = useSelector((state: any) => state.accountApplications.deleteDocument.isLoading);
  const isDeleteDocumentError = useSelector((state: any) => Boolean(state.accountApplications.deleteDocument.__failed));
  const deleteDocumentErrorMessage = useSelector(
    (state: any) => state.accountApplications.deleteDocument.error?.message,
  );
  const isDeleteDocumentSuccess = useSelector((state: any) =>
    Boolean(state.accountApplications.deleteDocument.__succeeded),
  );

  const isCancelDocumentLoading = useSelector((state: any) => state.accountApplications.cancelDocument.isLoading);
  const isCancelDocumentError = useSelector((state: any) => Boolean(state.accountApplications.cancelDocument.__failed));
  const cancelDocumentErrorMessage = useSelector(
    (state: any) => state.accountApplications.cancelDocument.error?.message,
  );
  const isCancelDocumentSuccess = useSelector((state: any) =>
    Boolean(state.accountApplications.cancelDocument.__succeeded),
  );

  const { account } = useAccountSelector();

  const isApplicationSubmitted = () => Boolean(account?.accountId);

  const getOptionList = () => {
    return (
      category?.requestedDocuments?.map(document => ({
        value: document.value,
        label: document.label,
      })) ?? []
    );
  };

  const onBeforeUpload = async (file: RcFile): Promise<boolean> => {
    try {
      await validateFile(file, { accept });
      setFileSize(file.size);

      return true;
    } catch (error: any) {
      setDocumentTypeError(error.message);

      return false;
    }
  };

  const onUploadChange = (info: Pick<UploadChangeParam, 'file'>) => {
    if (info.file.status === 'uploading') {
      setDocumentTypeError(undefined);
      setOriginalFilename(info.file.name);
      setIsUploading(true);
      setFileList([{ name: info.file.name, uid: info.file.uid }]);
    }

    // NOTE: info.file.erorr equals the error passed down from the onError() callback on unUpload() method
    if (info.file.status === 'error') {
      setDocumentTypeError(info.file.error?.message);
      setIsUploading(false);
    }

    if (info.file.status === 'done') {
      setDocumentTypeError(undefined);
      setIsUploading(false);
      setFileList([]);
    }
  };

  const onUpload = async ({
    file,
    onSuccess,
    onError,
  }: Pick<UploadRequestOption, 'file' | 'onSuccess' | 'onError'>) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('originalFilename', originalFilename ?? '');
      formData.append('type', documentType ?? '');
      formData.append('size', fileSize.toString());

      const document = await clientApi.accountApplication.uploadDocument({
        authToken,
        body: formData,
        params: { id: account?.applicationId, accountId: account?.id },
        onProgress: setUploadPercent,
      });
      dispatch(
        doRefreshAccountApplication({
          params: { id: account?.applicationId, accountId: account?.id },
          key: category.key,
          documentId: document.id,
        }),
      );

      if (onSuccess) {
        onSuccess({});
      }
    } catch (error: any) {
      if (error.response?.data) {
        onError?.(new Error(error.response.data.message));

        return;
      }

      if (error?.message) {
        onError?.(error);

        return;
      }

      onError?.(new UnknownError(ErrorCode.UPLOAD_APPLICATION_DOCUMENT));
    }
  };

  const onCancel = (id: string) => {
    setDeleteMode('cancel');
    setIsDeleteConfirmModalVisible(true);
    setDeleteDocumentId(id);
  };

  const onRemove = (id: string) => {
    setDeleteMode('remove');
    setIsDeleteConfirmModalVisible(true);
    setDeleteDocumentId(id);
  };

  const onConfirmFileDeletion = async () => {
    dispatch(
      doDeleteAccountApplicationDocument({
        params: { id: account?.applicationId, accountId: account?.id, documentId: deleteDocumentId },
        key: category.key,
      }),
    );
  };

  const onConfirmFileCancelation = async () => {
    dispatch(
      doCancelAccountApplicationDocument({
        params: { id: account?.applicationId, accountId: account?.id, documentId: deleteDocumentId },
        key: category.key,
      }),
    );
  };

  const onCancelFileDeletion = () => {
    setIsDeleteConfirmModalVisible(false);
    setDeleteDocumentId(undefined);
    setDeleteMode(undefined);
  };

  const onClose = () => {
    dispatch(doClearDeleteAccountApplicationDocument());
    dispatch(doClearCancelAccountApplicationDocument());
    onCancelFileDeletion();
  };

  useEffect(() => {
    if (isDeleteDocumentSuccess || isCancelDocumentSuccess) {
      onClose();
    }
  }, [isDeleteDocumentSuccess, isCancelDocumentSuccess]);

  return (
    <>
      <MUpload
        label={label}
        accept={accept}
        value={documentType}
        defaultValue={category.requestedDocuments[0].value}
        options={getOptionList()}
        error={documentTypeError || form.errors[category.name.value]}
        isLoading={isUploading}
        fileList={fileList}
        percent={uploadPercent}
        onChange={value => setDocumentType(value)}
        beforeUpload={onBeforeUpload}
        onUploadChange={onUploadChange}
        onUpload={onUpload}
        uploadedFileList={
          category.isResolved
            ? category.uploadedDocuments?.map(doc => ({
                id: doc.id,
                name: doc.originalFilename,
                type: category.name.isGovernmentId ? doc.type.label : undefined,
              }))
            : []
        }
        onCancelUploadedFile={isApplicationSubmitted() ? undefined : isCancelable ? onCancel : undefined}
        onDeleteUploadedFile={isApplicationSubmitted() ? undefined : isDeletable ? onRemove : undefined}
        deleteUploadedFileLabel={title ? `Remove ${title}` : undefined}
        testId={testId}
      />
      <ConfirmActionModal
        title={deleteMode === 'cancel' ? 'Cancel File Upload' : `Remove ${title}`}
        message={
          deleteMode === 'cancel'
            ? 'Are you sure you want to cancel the uploaded file? If yes, the file will be removed and you will need to upload it again.'
            : `Are you sure you want to remove ${title}?`
        }
        isOpen={isDeleteConfirmModalVisible}
        onOk={deleteMode === 'cancel' ? onConfirmFileCancelation : onConfirmFileDeletion}
        onCancel={onCancelFileDeletion}
        isLoading={isDeleteDocumentLoading || isCancelDocumentLoading}
        isError={isDeleteDocumentError || isCancelDocumentError}
        errorMessage={deleteDocumentErrorMessage || cancelDocumentErrorMessage}
        onClose={onClose}
      />
    </>
  );
};
